import React from 'react';

import { Box, Grid, Typography } from '@mui/material';

import { TextField, usePermissions, useRecordContext } from 'react-admin';

import SummaryCard from './SummaryCard';
import RuleLogic from './RuleLogic';
import RuleTriggers from './RuleTriggers';
import RuleResponse from './RuleResponse';
import RuleScore from './RuleScore';
import NotifyEmail from '../../NotifyEmail';
import TagArraySelectInput from '../../../../../customFields/TagArraySelectInput';
import RuleCasePriorityInput from '../../../../../customFields/RuleCasePriorityInput';
import { Rule } from '../../../../../utilities/schemas/rules';
import ResetStatisticalTracking from './ResetStatisticalTracking';
import hasAccess from '../../../../../utilities/hasAccess';
import actions, { Actions } from '../../../../../constants/actions';
import { usePendingReviewBlocked } from '../../../../../context/PendingReviewContext';

const RuleSummary = () => {
  const record = useRecordContext<Rule>();
  const { permissions } = usePermissions<Actions[]>();

  const isEditingExistingRule = !!record?.id;
  const pendingReview = usePendingReviewBlocked();
  const isDraft = !record?.versions;
  const isEditable = isDraft && !pendingReview;
  const createPing = record?.createPing;

  return (
    <Grid gap={4} container>
      {isEditingExistingRule && (
        <Grid size={12}>
          <SummaryCard canEdit={false} title="Rule identifiers">
            <Grid container spacing={1}>
              <Grid size={6}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="label" color="black" fontWeight="bold">Rule id</Typography>
                  <TextField source="id" label="ID" />
                </Box>
              </Grid>
              <Grid size={6}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="label" color="black" fontWeight="bold">Origination rule id</Typography>
                  <TextField source="originationChainId" emptyText="-" />
                </Box>
              </Grid>
            </Grid>
          </SummaryCard>
        </Grid>
      )}
      <Grid size={12}>
        <SummaryCard canEdit={isEditable} path="" title="Rule logic">
          <RuleLogic />
        </SummaryCard>
      </Grid>
      <Grid size={12}>
        <SummaryCard canEdit={isEditable} path="triggers" title="Rule triggers">
          <RuleTriggers />
        </SummaryCard>
      </Grid>
      <Grid size={12}>
        <SummaryCard canEdit={isEditable} path="response" title="Rule response">
          <RuleResponse />
        </SummaryCard>
      </Grid>
      {createPing && (
        <Grid size={12}>
          <SummaryCard canEdit={isEditable} path="score" title="Score calculation">
            <RuleScore />
          </SummaryCard>
        </Grid>
      )}
      {hasAccess(permissions, actions.TAG_LIST) && (
        <Grid size={12}>
          <TagArraySelectInput disabled={!isEditable} />
        </Grid>
      )}
      <Grid size={12}>
        <RuleCasePriorityInput disabled={!isEditable} />
      </Grid>
      {createPing && (
        <Grid size={12}>
          <NotifyEmail disabled={!isEditable} />
        </Grid>
      )}
      {isEditingExistingRule && (
        <Grid size={12}>
          <ResetStatisticalTracking disabled={!isEditable} />
        </Grid>
      )}
    </Grid>
  );
};

export default RuleSummary;
